/**
 * OnDemandLiveRegion
 * @module OnDemandLiveRegion
 * @description A utility class for creating an on-demand live region for screen readers.
 * This class allows you to create a live region that can be updated with messages
 * dynamically, and it will announce the messages to screen readers.
 * It is useful for providing feedback to users in real-time, such as form validation messages or status updates.
 * @example
 * const liveRegion = new OnDemandLiveRegion({
 *   level: 'assertive',
 *   parent: '#live-region-container',
 *   idPrefix: 'live-region-',
 *   delay: 500
 * });
 * liveRegion.sayMessage('This is a test message', 1000);
 * @param {Object} options - Configuration options for the live region.
 * @param {string} options.level - The aria-live level for the live region. Can be 'polite' or 'assertive'.
 * @param {string} options.parent - The parent element where the live region will be appended.
 * @param {string} options.idPrefix - The prefix for the live region's ID.
 * @param {number} options.delay - The delay in milliseconds before the message is spoken.
 * @constructor
 * @classdesc
 * This class creates a live region that can be updated with messages dynamically.
 * The live region is hidden visually but will be announced by screen readers.
 * The class provides a method to add messages to the live region, which will be spoken by screen readers.
 * The live region is created with the specified aria-live level, role, and ID prefix.
 * The live region is appended to the specified parent element, and the message is spoken after a specified delay.
 * The live region is removed from the DOM after the message is spoken.
 * The class is designed to be used in web applications to provide real-time feedback to users.
 */
export default class OnDemandLiveRegion {
  constructor(options) {
    this.options = options || {};
    this.settings = {
      level: "polite",
      parent: "body",
      idPrefix: "live-region-",
      delay: 0,
    };
    for (let setting in this.options) {
      if (this.options.hasOwnProperty(setting)) {
        this.settings[setting] = this.options[setting];
      }
    }
    this.settings.parent = document.querySelector(this.settings.parent);
  }

  /**
   * Add a message to the live region
   * @param {string} thingToSay - The message to be spoken
   * @param {number} delay - Delay in milliseconds before the message is spoken
   */
  sayMessage(thingToSay, delay) {
    const oldRegion =
      this.settings.parent.querySelector(
        '[id^="' + this.settings.idPrefix + '"]',
      ) || false;
    if (oldRegion) {
      this.settings.parent.removeChild(oldRegion);
    }
    if (delay) {
      this.settings.delay = delay;
    }
    // Create fresh live region
    this.currentRegion = document.createElement("span");
    this.currentRegion.id =
      this.settings.idPrefix + Math.floor(Math.random() * 10000);
    // Determine redundant role
    const role = this.settings.level !== "assertive" ? "status" : "alert";
    // Add role and aria-live attribution
    this.currentRegion.setAttribute("aria-live", this.settings.level);
    this.currentRegion.setAttribute("role", role);
    // Hide live region element, but not from assistive technologies
    this.currentRegion.classList.add("sr-only");
    // Add live region to its designated parent
    this.settings.parent.appendChild(this.currentRegion);
    // Populate live region to trigger it
    window.setTimeout(
      function () {
        this.currentRegion.textContent = thingToSay;
      }.bind(this),
      this.settings.delay,
    );
  }
}
